import { Inject } from '@odinjs/odin';
import { FieldAPI, FormAPI, InputAPI, ModalAPI, PanelAPI, RadioGroupAPI, RecaptchaAPI, SelectableItem, TextAPI, WizardAPI, WizardStepAPI } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnAfterNavigate, OnBeforeClear, OnBlur, OnChange, OnClick, OnClose, OnMount, OnNavigate, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import { AsyncAbortFunction } from '@philips-internal/oswald-ui-core/lib/component/event';
import { AbortableClearEvent, AbortableNavigateEvent, ChangeEvent, FocusEvent, MountEvent, NavigateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, LanguageService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { Validation, Validator } from '@philips-internal/oswald-ui-core/lib/services/model-service';
import { LocaleTypes } from '../../../src/constants/locale-types';
import { ForgetPasswordAliases } from '../../codegen';
import { HttpStatus } from '../../constants/HttpStatus';
import { PHYSICAL_PERSON_CODE_LENGTH_VALIDATION, USERNAME_MAX_LENGTH_VALIDATION, USERNAME_MIN_LENGTH_VALIDATION_FORGET_PASSWORD } from '../../constants/application-constants';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import { DocumentType } from '../../constants/document-type';
import { FieldValidatorsName } from '../../constants/validators-name-constant';
import { HttpServiceError, PatientHttpService } from '../../services/patient-http-service';
import ResetPasswordService, { RecoveryDataProps } from '../../services/reset-password-service';
import { getCatpchaPublicKey } from '../../utils/captcha-utils';
import { DocumentTypeDefinitions } from '../../utils/document-type';
import { showToastNotificationError, showToastNotificationSuccess, showToastNotificationWarning } from '../../utils/toast-notification-utils';
import { WizardUtils } from '../../utils/wizard-utils';
import BaseController from '../BaseController';
import './forget-password-controller.css';

interface IdentificationTypeSelectable extends SelectableItem {
  value: IdentificationType;
}
interface FormUsernameProps extends Record<string, Value> {
  documentType?: string;
  identificationType: IdentificationTypeSelectable,
  physicalPersonCode?: string;
  username?: string;
}

interface FormDsChaveSmsProps extends Record<string, Value> {
  smsCode: string;
}

interface FormPasswordProps extends Record<string, Value> {
  captchaResponse?: string;
  newPassword: string;
  newPasswordConfirmation: string;
}

interface RecoveryTypeSelectable extends SelectableItem {
  value: RecoveryType;
}

interface FormRecoveryTypeProps extends Record<string, Value> {
  captchaResponse?: string;
  recoveryType: RecoveryTypeSelectable,
}

enum IdentificationType {
  PHYSICAL_PERSON_CODE = 'C',
  USERNAME_OR_EMAIL = 'U'
}

enum RecoveryType {
  EMAIL = 'E',
  SMS = 'S'
}

enum WizardForgetPasswordSteps {
  STEP_USERNAME = 'step-username',
  STEP_RECOVERY_TYPE = 'step-recovery-type',
  STEP_VERIFICATION_CODE = 'step-verification-code',
  STEP_NEW_PASSWORD = 'step-new-password',
}

enum ContextKeys {
  IS_SMS_CODE_VALID = 'isSmsCodeValid',
  DEFAULT_IDENTIFICATION_TYPE = 'defaultIdentificationType'
}

enum LocalValidatorNames {
  SMS_INVALID_CODE = 'sms-invalid-code',
}

interface PhysicalPersonCodeForcedChangeProps {
  forcedOnChange?: {
    visible: boolean;
  }
}

interface PasswordResetProps extends Record<string, Value> {
  captchaResponse?: string;
  recoveryDocumentType?: string;
  recoveryKey: string;
  recoveryKeyType: IdentificationType;
  recoveryType: RecoveryType;
  resetPasswordWebLab?: ResetPasswordWebLab;
}

interface ResetPasswordWebLab {
  confirmPassword?: string;
  isLaboratoryWebUser: boolean;
  password?: string;
  patientId: string;
  username?: string;
}

interface DataPasswordResetProps extends Record<string, Value> {
  captchaResponse?: string;
  newPassword: string;
  newPasswordConfirmation: string;
  recoveryDocumentType?: string;
  recoveryKey: string;
  recoveryKeyType: IdentificationType;
  recoveryType: RecoveryType;
}

@Controller({ component: 'root-modal', fragment: 'forget-password' })
class ForgetPasswordController extends BaseController {

  wizardForgetPasswordAPI!: WizardAPI;

  @Inject('panel-loader-action')
    panelLoader!: PanelAPI;

  @Inject('field-username')
    fieldUsernameAPI!: FieldAPI;

  @Inject('field-physical-person-code')
    fieldPhysicalPersonCodeAPI!: FieldAPI;

  @Inject('form-username')
    formUsernameAPI!: FormAPI;

  @Inject('rg-recovery-types')
    radioGroupRecoveryTypesAPI!: RadioGroupAPI;

  @Inject('rg-identification-type')
    radioGroupIdentificationTypeAPI!: RadioGroupAPI;

  @Inject('panel-loader-action')
    panelLoaderAPI!: PanelAPI;

  @Inject('field-username-view')
    fieldUsernameViewAPI!: FieldAPI;

  @Inject('field-physical-person-code-view')
    fieldPhysicalPersonCodeViewAPI!: FieldAPI;

  @Inject('form-recovery-type')
    formRecoveryTypeAPI!: FormAPI;

  @Inject('form-recovery-type-view')
    formRecoveryTypeViewAPI!: FormAPI;

  @Inject('field-identification-type')
    fieldIdentificationTypeAPI!: FieldAPI;

  @Inject('field-recaptcha-email')
    fieldRecaptchaEmailAPI!: FieldAPI;

  @Inject('field-recaptcha-sms')
    fieldRecaptchaSmsAPI!: FieldAPI;

  @Inject('root-modal')
    rootModalAPI!: ModalAPI;

  @Inject('wz-recovery-type')
    wizardStepRecoveryTypeAPI!: WizardStepAPI;

  @Inject('panel-loader-resend-sms-code')
    panelLoaderResendSmsCodeAPI!: PanelAPI;

  @Inject('panel-link-resend-code')
    panelLinkResendCodeAPI!: PanelAPI;

  @Inject('text-information-resend-sms-code')
    textInformationResendSmsCode!: TextAPI;

  @Inject('form-ds-chave-sms')
    formDsChaveSmsAPI!: FormAPI;

  @Inject('field-ds-chave-sms')
    fieldDsChaveSmsAPI!: FieldAPI;

  @Inject('field-password')
    fieldPasswordAPI!: FieldAPI;

  @Inject('field-confirm-password')
    fieldConfirmPasswordAPI!: FieldAPI;

  @Inject('password-form')
    formPasswordAPI!: FormAPI;

  @Inject('password-form-view')
    formPasswordViewAPI!: FormAPI;

  //for web lab user with email
  @Inject(ForgetPasswordAliases.LOGIN_WEB_LAB_FIELD)
    loginWebLabFieldpi!: FieldAPI;

  @Inject(ForgetPasswordAliases.PASSWORD_WEB_LAB_FIELD)
    passwordWebLabFieldpi!: FieldAPI;

  @Inject(ForgetPasswordAliases.PASSWORD_CONFIRM_WEB_LAB_FIELD)
    passwordConfirmWebLabFieldpi!: FieldAPI;

  @Inject(ForgetPasswordAliases.FIELD_DOCUMENT_TYPE)
    documentTypeFieldpi!: FieldAPI;

  @Inject(ForgetPasswordAliases.INPUT_PHYSICAL_PERSON_CODE)
    inputPhysicalPersonCodeAPI!: InputAPI;

  private pendingNextSteps: Map<WizardForgetPasswordSteps, boolean> = new Map();

  private intervalsToClear: Map<WizardForgetPasswordSteps, NodeJS.Timeout> = new Map();

  @OnMount([
    ForgetPasswordAliases.RECAPTCHA_EMAIL,
    ForgetPasswordAliases.RECAPTCHA_SMS
  ])
  onMountRecaptcha(event: MountEvent): void {
    const recaptchaAPI = event.target as RecaptchaAPI;
    switch(recaptchaAPI.getAlias()) {
      case ForgetPasswordAliases.RECAPTCHA_EMAIL:
        this.handleRecaptchaLoading(this.fieldRecaptchaEmailAPI, recaptchaAPI);
        break;
      case ForgetPasswordAliases.RECAPTCHA_SMS:
        this.handleRecaptchaLoading(this.fieldRecaptchaSmsAPI, recaptchaAPI);
        break;
      default:
        break;
    }
  }

  handleRecaptchaLoading(fieldAPI: FieldAPI, recaptchaAPI: RecaptchaAPI): void {
    if (!fieldAPI.isVisible()) {
      return;
    }

    getCatpchaPublicKey().then(async publicKey => {
      if (publicKey && publicKey != null && publicKey.length > 0){
        recaptchaAPI.setSiteKey(publicKey);
      } else {
        fieldAPI.removeValidator('required');
        await fieldAPI.validate();
        fieldAPI.setVisible(false);
      }
    });
  }

  async handleRecaptchaOnDestroy(fieldAPI: FieldAPI): Promise<void> {
    fieldAPI.removeValidator('required');
    await fieldAPI.validate();
    fieldAPI.setVisible(false);
  }

  @OnChange({target: ForgetPasswordAliases.LOGIN_WEB_LAB_FIELD})
  async onEmailChange():Promise<void>{
    const usernameAlreadyExists = this.context.get('usernameAlreadyExists');
    if(usernameAlreadyExists){
      this.loginWebLabFieldpi.removeLocalValidator('patient.username.already.exist');
      await this.loginWebLabFieldpi.validate();
      await this.formRecoveryTypeAPI.validate();
    }
  }

  @OnClose({ target: 'root-modal' })
  onRootModalClose(): void {
    for (const interval of this.intervalsToClear.values()) {
      if(interval) {
        clearInterval(interval);
      }
    }

    this.historyService.push(APPLICATION_ROUTES.LOGIN);
  }

  @OnChange({ target: 'rg-identification-type' })
  handleChangeRadioGroupIdentifyBy(event: ChangeEvent<SelectableItem>): void {
    switch(event.value.value) {
      case IdentificationType.PHYSICAL_PERSON_CODE:
        this.handlePhysicalPersonCodeOption();
        break;
      case IdentificationType.USERNAME_OR_EMAIL:
        this.handleUsernameOrEmailOption();
        this.adjustFieldsVisibility(event);
        break;
    }
  }

  @OnMount(ForgetPasswordAliases.RG_RECOVERY_TYPES)
  onMountLayoutForgetPassword(): void {
    this.fieldRecaptchaEmailAPI.setVisible(false);
    this.fieldRecaptchaSmsAPI.setVisible(false);
  }

  @OnChange('rg-recovery-types')
  async handleChangeRadioGroupRecoveryTypes(event: ChangeEvent<RecoveryTypeSelectable>): Promise<void> {
    const optionSelected = event.value;

    if (optionSelected.value === RecoveryType.EMAIL) {
      this.wizardForgetPasswordAPI.setNextStepExpression('tws.wizard.confirm');
      this.handleRecaptchaOnDestroy(this.fieldRecaptchaSmsAPI);
      this.fieldRecaptchaEmailAPI.setVisible(true);
    } else {
      await this.formRecoveryTypeAPI.validate();
      this.handleRecaptchaOnDestroy(this.fieldRecaptchaEmailAPI);
      this.fieldRecaptchaSmsAPI.setVisible(true);
      this.wizardForgetPasswordAPI.setNextStepExpression('tws.wizard.next-step');
    }

  }

  private handleUsernameOrEmailOption(): void {
    this.fieldPhysicalPersonCodeAPI.setValue(undefined);
    this.fieldPhysicalPersonCodeAPI.setVisible(false);
    this.fieldUsernameAPI.setVisible(true);

    this.fireForcedChangeToRecoveryKeyFields(this.fieldUsernameAPI, this.fieldPhysicalPersonCodeAPI);
  }

  private handlePhysicalPersonCodeOption(): void {
    this.fieldUsernameAPI.setValue(undefined);
    this.fieldUsernameAPI.setVisible(false);
    this.fieldPhysicalPersonCodeAPI.setVisible(true);
    this.fireForcedChangeToRecoveryKeyFields(this.fieldUsernameAPI, this.fieldPhysicalPersonCodeAPI);
  }

  private fireForcedChangeToRecoveryKeyFields(fieldUsernameAPI: FieldAPI, fieldPhysicalPersonCodeAPI: FieldAPI): void {
    fieldUsernameAPI.onChange.fire(
      new ChangeEvent<PhysicalPersonCodeForcedChangeProps>({
        value: {
          forcedOnChange: { visible: fieldUsernameAPI.isVisible() }
        }
      })
    );

    fieldPhysicalPersonCodeAPI.onChange.fire(
      new ChangeEvent<PhysicalPersonCodeForcedChangeProps>({
        value: {
          forcedOnChange: {
            visible: fieldPhysicalPersonCodeAPI.isVisible()
          }
        }
      })
    );
  }

  @OnBeforeClear('wz-forget-password')
  handleWizarStepsClearAction(event: AbortableClearEvent): void {
    const { entry } = event;
    if(!entry) {
      return;
    }

    switch(entry) {
      case WizardForgetPasswordSteps.STEP_USERNAME:
        this.clearStepUsername();
        break;
      case WizardForgetPasswordSteps.STEP_RECOVERY_TYPE:
        this.clearStepRecoveryType();
        break;
      case WizardForgetPasswordSteps.STEP_VERIFICATION_CODE:
        this.clearStepVerificationCode();
        break;
      case WizardForgetPasswordSteps.STEP_NEW_PASSWORD:
        this.clearStepNewPassword();
        break;
      default:
        break;
    }
  }

  private clearStepUsername(): void {
    const defaultIdentificationType = this.context.get(ContextKeys.DEFAULT_IDENTIFICATION_TYPE) as SelectableItem;
    this.formUsernameAPI.setRecord({ identificationType: defaultIdentificationType });
    this.radioGroupIdentificationTypeAPI.onChange.fire(new ChangeEvent({ value: defaultIdentificationType }));
  }

  private clearStepRecoveryType(): void {
    this.formRecoveryTypeAPI.setRecord({});
  }

  private clearStepVerificationCode(): void {
    this.formDsChaveSmsAPI.setRecord({});
  }

  private clearStepNewPassword(): void {
    this.formPasswordAPI.setRecord({});
  }

  @OnAfterNavigate('wz-forget-password')
  handleWizardForgetPasswordAfterNavigate(event: NavigateEvent<string>): void {
    const { to, from } = event;

    if (to === WizardForgetPasswordSteps.STEP_NEW_PASSWORD) {
      this.wizardForgetPasswordAPI.setStepDone(to, true);
    }

    if (from === WizardForgetPasswordSteps.STEP_RECOVERY_TYPE) {
      const countInterval = this.intervalsToClear.get(from);
      if (countInterval) {
        clearInterval(countInterval);
      }
    }
  }

  @OnNavigate('wz-forget-password')
  handleOnNavigateWizardForgetPassword(event: AbortableNavigateEvent<string>): void {
    const { abort, from, to } = event;

    const wizardSteps = this.wizardForgetPasswordAPI.getSteps();
    const isPreviousStep = WizardUtils.isPreviousStep(wizardSteps, to, from);
    if(isPreviousStep) {
      return;
    }

    if (!this.currentWizardNavigationIsValid(from) || !to) {
      abort(Promise.resolve(true));
      return;
    }

    // Must be called after the necessary validations.
    switch(to) {
      case WizardForgetPasswordSteps.STEP_RECOVERY_TYPE:
        this.handleSomethingBeforeGoingToStepRecoveryType(abort);
        break;
      case WizardForgetPasswordSteps.STEP_USERNAME:
        this.handleSomethingBeforeGoingToStepUsername();
        break;
      case WizardForgetPasswordSteps.STEP_VERIFICATION_CODE:
        this.handleSomethingBeforeGoingToStepVerificationCode(abort);
        break;
      case WizardForgetPasswordSteps.STEP_NEW_PASSWORD:
        this.handleSomethingBeforeGoingToStepNewPassword(abort);
        break;
      default:
        break;
    }
  }

  private async currentWizardNavigationIsValid(from: string): Promise<boolean> {
    switch(from) {
      case WizardForgetPasswordSteps.STEP_USERNAME:
        return await this.validateStepUsername();
      case WizardForgetPasswordSteps.STEP_RECOVERY_TYPE:
        return await this.validateStepRecoveryType();
      case WizardForgetPasswordSteps.STEP_VERIFICATION_CODE:
        return this.validateStepVerificationCode();
      default:
        return false;
    }
  }

  private async validateStepUsername(): Promise<boolean> {
    return await this.formUsernameAPI.validate();
  }

  private async validateStepRecoveryType(): Promise<boolean> {
    const isFormValid = await this.formRecoveryTypeAPI.validate();
    if (!isFormValid) {
      return false;
    }

    const formRecoveryTypeRecord = this.formRecoveryTypeAPI.getRecord() as FormRecoveryTypeProps;
    if(formRecoveryTypeRecord.recoveryType.value && formRecoveryTypeRecord.recoveryType.value === 'S'){
      return true;
    }
    if(!this.context.get('isLaboratoryWebUser') && formRecoveryTypeRecord.recoveryType.value && formRecoveryTypeRecord.recoveryType.value === 'E'){
      return true;
    }

    return isFormValid;
  }

  private handleSomethingBeforeGoingToStepUsername(): void {
    this.radioGroupRecoveryTypesAPI.setData([]);
  }

  private async validateStepVerificationCode(): Promise<boolean> {
    return await this.formDsChaveSmsAPI.validate();
  }

  private handleSomethingBeforeGoingToStepVerificationCode(abort: AsyncAbortFunction): void {
    const isPending = this.pendingNextSteps.get(WizardForgetPasswordSteps.STEP_VERIFICATION_CODE);

    if(isPending) {
      abort(Promise.resolve(true));
      return;
    }

    this.pendingNextSteps.set(WizardForgetPasswordSteps.STEP_VERIFICATION_CODE, true);
    const formRecoveryTypeRecord = this.formRecoveryTypeAPI.getRecord() as FormRecoveryTypeProps;
    const formUsernameRecord = this.formUsernameAPI.getRecord() as unknown as FormUsernameProps;

    const isLaboratoryWebUser = this.context.get('isLaboratoryWebUser') as boolean;
    const patientId = this.context.get('patientId') as string;

    const resetPasswordWebLab: ResetPasswordWebLab = {
      confirmPassword: this.passwordConfirmWebLabFieldpi.getValue() as string,
      isLaboratoryWebUser,
      password: this.passwordWebLabFieldpi.getValue() as string,
      patientId,
      username: this.loginWebLabFieldpi.getValue() as string
    }

    const body: PasswordResetProps = {
      captchaResponse: formRecoveryTypeRecord.captchaResponse,
      recoveryDocumentType: formUsernameRecord.documentType,
      recoveryKey: (formUsernameRecord.username ?? formUsernameRecord.physicalPersonCode) as string,
      recoveryKeyType: formUsernameRecord.identificationType.value,
      recoveryType: formRecoveryTypeRecord.recoveryType.value,
      resetPasswordWebLab
    }

    this.setStepRecoveryTypeInProgress(true);
    const { recoveryType } = this.formRecoveryTypeAPI.getRecord() as FormRecoveryTypeProps;

    abort(
      ResetPasswordService.postResetPassword(body).then((res) => {
        if (recoveryType.value === RecoveryType.EMAIL) {
          this.closeModal();
          this.showToastRecoveryTypeByEmailSuccess(isLaboratoryWebUser);
          return true;
        }
        this.context.set({'dsLogin': res.body?.username});

        return false;
      }).catch(async ({ response }: HttpServiceError) => {
        this.panelLoaderAPI.setVisible(false);
        if (response?.status === HttpStatus.CONFLICT) {
          this.context.set({usernameAlreadyExists:true});
          this.loginWebLabFieldpi.addLocalValidator('patient.username.already.exist', async () => {
            return [{
              message: MessageService.translate('patient.username.already.exist'),
              name: 'patient.username.already.exist',
              valid: false
            }];
          });
          await this.loginWebLabFieldpi.validate();
        }
        this.handlePasswordFailure();
        return true;
      }).finally(() => {
        this.pendingNextSteps.set(WizardForgetPasswordSteps.STEP_VERIFICATION_CODE, false);
        this.setStepRecoveryTypeInProgress(false);
      })
    );
  }

  private handleSomethingBeforeGoingToStepNewPassword(abort: AsyncAbortFunction): void {
    const isPending = this.pendingNextSteps.get(WizardForgetPasswordSteps.STEP_NEW_PASSWORD);

    if(isPending) {
      abort(Promise.resolve(true));
      return;
    }

    const isSmsCodeValid = this.context.get(ContextKeys.IS_SMS_CODE_VALID);
    if (isSmsCodeValid) {
      return;
    }

    this.panelLoaderResendSmsCodeAPI.setVisible(true);
    const { smsCode } = this.formDsChaveSmsAPI.getRecord() as FormDsChaveSmsProps;

    const formRecoveryTypeRecord = this.formRecoveryTypeAPI.getRecord() as FormRecoveryTypeProps;
    const formUsernameRecord = this.formUsernameAPI.getRecord() as unknown as FormUsernameProps;

    this.pendingNextSteps.set(WizardForgetPasswordSteps.STEP_NEW_PASSWORD, true);

    const isLaboratoryWebUser = this.context.get('isLaboratoryWebUser') as boolean;
    const patientId = this.context.get('patientId') as string;

    const resetPasswordWebLab: ResetPasswordWebLab = {
      confirmPassword: this.passwordConfirmWebLabFieldpi.getValue() as string,
      isLaboratoryWebUser,
      password: undefined,
      patientId,
      username: undefined
    }

    const body: PasswordResetProps = {
      captchaResponse: formRecoveryTypeRecord.captchaResponse,
      recoveryDocumentType: formUsernameRecord.documentType,
      recoveryKey: (formUsernameRecord.username ?? formUsernameRecord.physicalPersonCode) as string,
      recoveryKeyType: formUsernameRecord.identificationType.value,
      recoveryType: formRecoveryTypeRecord.recoveryType.value,
      resetPasswordWebLab
    }

    abort(
      PatientHttpService.post(APPLICATION_ENDPOINTS.VALIDATE_SMS_CODE, { smsCode }, { body }).then(() => {
        this.handleValidationSmsCodeSuccess(isLaboratoryWebUser);
        return false;
      }).catch(({ response }: HttpServiceError) => {
        this.handleValidationSmsCodeFail(response?.status);
        return true;
      }).finally(() => {
        this.pendingNextSteps.set(WizardForgetPasswordSteps.STEP_NEW_PASSWORD, false);
      })
    );
  }

  private closeModal = (): void => {
    this.historyService.back();
  }

  private handleValidationSmsCodeSuccess(isLaboratoryWebUser: boolean):void{
    this.panelLoaderResendSmsCodeAPI.setVisible(false);
    this.panelLinkResendCodeAPI.setVisible(false);
    this.context.set({ [ContextKeys.IS_SMS_CODE_VALID]: true });
    if(isLaboratoryWebUser){
      showToastNotificationSuccess('patient.web.lab.password.desativate', undefined, undefined, 'newaccount.text.successfully.activation');

      for (const interval of this.intervalsToClear.values()) {
        if(interval) {
          clearInterval(interval);
        }
      }
      this.historyService.push(APPLICATION_ROUTES.LOGIN);
    }
  }

  private async handleValidationSmsCodeFail(status = 500) : Promise<void>{
    if(status === 422){
      this.fieldDsChaveSmsAPI.addLocalValidator(LocalValidatorNames.SMS_INVALID_CODE, async () => {
        return [{
          message: MessageService.translate('patient.incorrect.key'),
          name: LocalValidatorNames.SMS_INVALID_CODE,
          valid: false
        }];
      });
      await this.fieldDsChaveSmsAPI.validate();
    } else {
      showToastNotificationError(MessageService.translate('newaccount.error.internal.server.error.try.again'));
    }

    this.context.set({ [ContextKeys.IS_SMS_CODE_VALID]: false });
    this.panelLoaderResendSmsCodeAPI.setVisible(false);
  }

  @OnBlur('field-ds-chave-sms')
  handleFieldDsChaveSmsOutFocus(event: FocusEvent): void {
    const fieldAPI = event.target as FieldAPI;
    fieldAPI.removeLocalValidator(LocalValidatorNames.SMS_INVALID_CODE);
    this.context.set({ [ContextKeys.IS_SMS_CODE_VALID]: false });

    setTimeout(async () => {
      await fieldAPI.validate();
    });
  }

  private showToastRecoveryTypeByEmailSuccess(isLaboratoryWebUser: boolean): void {
    if (isLaboratoryWebUser) {
      showToastNotificationSuccess('patient.web.lab.password.desativate', undefined, undefined, 'newaccount.text.successfully.activation');
    } else {
      showToastNotificationSuccess('patient.forget.password.success.message', undefined, undefined, 'patient.forget.password.request.success');
    }

  }

  private showToastRecoveryTypeBySmsSuccess(): void {
    showToastNotificationSuccess('patient.message.password-success', undefined, undefined, 'patient.change.password');
    this.closeModal();
  }

  private handleSomethingBeforeGoingToStepRecoveryType(abort: AsyncAbortFunction): void {
    const isPending = this.pendingNextSteps.get(WizardForgetPasswordSteps.STEP_RECOVERY_TYPE);

    if(isPending) {
      abort(Promise.resolve(true));
      return;
    }

    const { documentType, identificationType, physicalPersonCode, username } = this.formUsernameAPI.getRecord() as unknown as FormUsernameProps;

    if(!physicalPersonCode && !username) {
      throw Error('At least one of the fields must exist');
    }

    this.panelLoaderAPI.setVisible(true);
    this.pendingNextSteps.set(WizardForgetPasswordSteps.STEP_RECOVERY_TYPE, true);

    const recoveryKey = (physicalPersonCode ?? username) as string;
    const selectedDocumentType = documentType as unknown as DocumentType;
    const recoveryDocumentType = selectedDocumentType ? selectedDocumentType.documentType : '';

    abort(
      ResetPasswordService.findUsernameRecoveryData(recoveryDocumentType, recoveryKey, identificationType.value).then(response => {
        const userNotFound = response && response.dsLoginTemp === 'TWS-0000000000';
        this.setRecoveryTypesOptions(response);
        if (userNotFound) {
          showToastNotificationWarning(MessageService.translate('patient.message.user-not-found'));
          return true;
        }
        return false;
      }).catch(error => {
        return this.handleStepRecoveryTypeError(error.status);
      }).finally(() => {
        this.pendingNextSteps.set(WizardForgetPasswordSteps.STEP_RECOVERY_TYPE, false);
        this.panelLoaderAPI.setVisible(false);
      })
    );
  }

  private handleStepRecoveryTypeError(status = 500): boolean {
    if ([404, 409].includes(status)) {
      showToastNotificationWarning(MessageService.translate('patient.message.user-not-found'));
    } else if(status === 400) {
      showToastNotificationWarning(MessageService.translate('patient.reset.password.not.allowed'));
    } else {
      showToastNotificationError(MessageService.translate('newaccount.error.internal.server.error.try.again'));
    }

    return true;
  }

  @OnMount('form-username-view')
  handleFormUsernameViewLoading(event: MountEvent): void {
    const formUsernameData = this.formUsernameAPI.getRecord() as FormUsernameProps;

    if (formUsernameData.physicalPersonCode) {
      this.fieldPhysicalPersonCodeViewAPI.setVisible(true);
    }

    if (formUsernameData.username) {
      this.fieldUsernameViewAPI.setVisible(true);
    }

    const formAPI = event.target as FormAPI;
    formAPI.setData({ ...formUsernameData,  identificationType: formUsernameData.identificationType.label });
  }

  @OnMount('form-recovery-type-view')
  handleFormRecoveryTypeViewLoading(event: MountEvent): void {
    const formRecoveryData = this.formRecoveryTypeAPI.getRecord() as FormRecoveryTypeProps;

    if(formRecoveryData && Object.keys(formRecoveryData).length > 0) {
      const formAPI = event.target as FormAPI;
      formAPI.setData({ recoveryType: formRecoveryData.recoveryType.label });
    }
  }

  @OnMount('form-ds-chave-sms-view')
  handleFormDsChaveSmsViewLoading(event: MountEvent): void {
    const formDsChaveSmsData = this.formDsChaveSmsAPI.getRecord() as FormDsChaveSmsProps;

    if(formDsChaveSmsData && Object.keys(formDsChaveSmsData).length > 0) {
      const formAPI = event.target as FormAPI;
      formAPI.setData(formDsChaveSmsData);
    }
  }

  @OnMount('field-identification-type')
  handleMountFieldIdentificationType(event: MountEvent): void {
    const fieldAPI = event.target as FieldAPI;

    if(LanguageService.getCurrentLanguage() === LocaleTypes.AUSTRALIA){
      fieldAPI.setVisible(false);
    }
  }

  @OnChange({ target: ForgetPasswordAliases.FIELD_IDENTIFICATION_TYPE})
  handleFieldIdentificationType(event: ChangeEvent<SelectableItem>): void {
    this.adjustFieldsVisibility(event);
  }

  adjustFieldsVisibility(event: ChangeEvent<SelectableItem>): void {
    if(LanguageService.getCurrentLanguage() !== LocaleTypes.COLOMBIA) {
      return;
    }
    switch (event.value.value) {
      case 'C':
        this.documentTypeFieldpi.setVisible(true);
        this.documentTypeFieldpi.addValidator({ name: 'required' });
        break;
      default:
        this.documentTypeFieldpi.setVisible(false);
        this.documentTypeFieldpi.removeValidator('required');
        this.documentTypeFieldpi.setValue(undefined);
        break;
    }
  }

  @OnMount({ target: 'rg-identification-type' })
  handleRadioGroupIdentifyByLoading(event: MountEvent): void {
    const radioGroupAPI = event.target as RadioGroupAPI;
    const indentifyTypes: SelectableItem[] = [
      {key: '2', label: 'patient.login-username-email', value: IdentificationType.USERNAME_OR_EMAIL}
    ];

    switch (LanguageService.getCurrentLanguage()) {
      case LocaleTypes.AUSTRALIA:
        break;
      case LocaleTypes.COLOMBIA:
        indentifyTypes.push({
          key: '1', label: 'patient.reset.document',
          value: IdentificationType.PHYSICAL_PERSON_CODE
        });
        break;
      default:
        indentifyTypes.push({
          key: '1', label: 'patient.profile.cpf',
          value: IdentificationType.PHYSICAL_PERSON_CODE
        });
        break;
    }

    radioGroupAPI.setData(indentifyTypes);
    const selectedValue = indentifyTypes[0];
    radioGroupAPI.setValue(selectedValue);
    radioGroupAPI.onChange.fire(new ChangeEvent({ value: selectedValue }));
    this.context.set({ [ContextKeys.DEFAULT_IDENTIFICATION_TYPE]: selectedValue });

    setTimeout(async () => {
      await this.fieldIdentificationTypeAPI.validate();
      this.wizardForgetPasswordAPI.setStepDone(WizardForgetPasswordSteps.STEP_USERNAME, false);
    });
  }

  private setRecoveryTypesOptions(recoveryData: RecoveryDataProps = {}): void {
    const { email, isLaboratoryWebUser, patientId, phoneNumber } = recoveryData;

    this.context.set({isLaboratoryWebUser, patientId});

    const recoveryTypes: SelectableItem[] = [];
    if(email) {
      recoveryTypes.push({ key: '1', label: `${MessageService.translate('patient.email')}: ${email}`, value: RecoveryType.EMAIL });
    }

    if(phoneNumber) {
      recoveryTypes.push({ key: '2', label: `${MessageService.translate('patient.sms')}: ${phoneNumber}`, value: RecoveryType.SMS });
    }

    if(recoveryTypes.length > 0) {
      this.radioGroupRecoveryTypesAPI.setData(recoveryTypes);
    }
    if(isLaboratoryWebUser && patientId){
      this.loginWebLabFieldpi.addValidator({ name: 'required' });
      this.loginWebLabFieldpi.setVisible(true);

      this.passwordWebLabFieldpi.addValidator({ name: 'required' });
      this.passwordWebLabFieldpi.addValidator({ name: 'cryptoPWValidator' });
      this.passwordWebLabFieldpi.setVisible(true);

      this.passwordConfirmWebLabFieldpi.addValidator({ name: 'required' });
      this.passwordConfirmWebLabFieldpi.addValidator({ name: 'cryptoPWConfirmValidator', options: { cryptoPW: this.passwordWebLabFieldpi.getValue() } });
      this.passwordConfirmWebLabFieldpi.setVisible(true);
      this.passwordConfirmWebLabFieldpi.addLocalValidator( 'password-match-validator', async () => { return await this.comparePassword(this.passwordWebLabFieldpi, this.passwordConfirmWebLabFieldpi) } )
    }
  }

  @OnChange({ target: ForgetPasswordAliases.FIELD_PHYSICAL_PERSON_CODE})
  handleFieldPhysicalPersonOnChange(event: ChangeEvent<PhysicalPersonCodeForcedChangeProps | Record<string, Value>>): void {
    const changedValue = event.value as PhysicalPersonCodeForcedChangeProps | undefined;

    const defaultValidators: Validator[] = [
      { name: 'required' },
      { name: 'min-length', options: { min: PHYSICAL_PERSON_CODE_LENGTH_VALIDATION } },
      { name: 'max-length', options: { min: USERNAME_MAX_LENGTH_VALIDATION }}
    ];

    this.setValidatorsOnFields(this.fieldPhysicalPersonCodeAPI, defaultValidators, changedValue);
  }

  @OnChange({ target: ForgetPasswordAliases.FIELD_USERNAME})
  handleFieldUsernameOnChange(event: ChangeEvent<PhysicalPersonCodeForcedChangeProps | Record<string, Value>>): void {
    const changedValue = event.value as PhysicalPersonCodeForcedChangeProps | undefined;

    const defaultValidators: Validator[] = [
      { name: 'required' },
      { name: 'min-length', options: { min: USERNAME_MIN_LENGTH_VALIDATION_FORGET_PASSWORD } },
      { name: 'max-length', options: { min: USERNAME_MAX_LENGTH_VALIDATION }}
    ];

    this.setValidatorsOnFields(this.fieldUsernameAPI, defaultValidators, changedValue);
  }

  private setValidatorsOnFields = (
    fieldAPI: FieldAPI,
    defaultValidators: Validator[],
    changedValue: PhysicalPersonCodeForcedChangeProps | undefined) : void => {

    // setTimeout needed because without it field.validate() doesn't work.
    setTimeout(async () => {
      if(!changedValue?.forcedOnChange) {
        return;
      }

      if (changedValue.forcedOnChange.visible) {
        fieldAPI.setValidators(defaultValidators);
        await fieldAPI.validate(false);
      } else {
        fieldAPI.setValidators([]);
        await fieldAPI.validate();
      }
    });
  }

  @OnMount({ target: 'wz-forget-password' })
  handleWizardAccountCreationLoading(event: MountEvent): void {
    this.wizardForgetPasswordAPI = event.target as WizardAPI;
  }

  @OnChange({ target: ForgetPasswordAliases.FORM_USERNAME })
  handleChangeUsernameForm(): void {
    if(!this.wizardForgetPasswordAPI.isStepDone(WizardForgetPasswordSteps.STEP_USERNAME)){
      this.wizardForgetPasswordAPI.setStepDone(WizardForgetPasswordSteps.STEP_USERNAME, true);
    }
  }

  @OnChange({ target: ForgetPasswordAliases.FORM_RECOVERY_TYPE })
  handleChangeRecoveryTypeForm(): void {
    this.wizardForgetPasswordAPI.setStepDone(WizardForgetPasswordSteps.STEP_RECOVERY_TYPE, true);
  }

  @OnChange({ target: ForgetPasswordAliases.FORM_DS_CHAVE_SMS })
  handleChangeVerificationCodeForm(): void {
    this.wizardForgetPasswordAPI.setStepDone(WizardForgetPasswordSteps.STEP_VERIFICATION_CODE, true);
  }

  @OnSubmit({ target: 'wz-forget-password'})
  async handleWizardForgetPasswordSubmit(): Promise<void> {

    if (!(await this.formPasswordAPI.validate())) {
      return;
    }

    const formDsChaveSmsRecord = this.formDsChaveSmsAPI.getRecord() as FormDsChaveSmsProps;
    const formPasswordRecord = this.formPasswordAPI.getRecord() as FormPasswordProps;
    const formRecoveryTypeRecord = this.formRecoveryTypeAPI.getRecord() as FormRecoveryTypeProps;
    const formUsernameRecord = this.formUsernameAPI.getRecord() as FormUsernameProps;

    this.setStepNewPasswordInProgress(true);

    const isLaboratoryWebUser = this.context.get('isLaboratoryWebUser') as boolean;
    const patientId = this.context.get('patientId') as string;

    const resetPasswordWebLab: ResetPasswordWebLab = {
      isLaboratoryWebUser,
      patientId
    }
    const body: DataPasswordResetProps = {
      captchaResponse: formPasswordRecord.captchaResponse,
      newPassword: formPasswordRecord.newPassword,
      newPasswordConfirmation: formPasswordRecord.newPasswordConfirmation,
      recoveryDocumentType: formUsernameRecord.documentType,
      recoveryKey: (formUsernameRecord.username ?? formUsernameRecord.physicalPersonCode) as string,
      recoveryKeyType: formUsernameRecord.identificationType.value,
      recoveryType: formRecoveryTypeRecord.recoveryType.value,
      resetPasswordWebLab
    }

    HttpService.post(
      APPLICATION_ENDPOINTS.RESET_PASSWORD_BY_SMS,
      { smsCode: formDsChaveSmsRecord.smsCode },
      { body }
    ).then((response) => {
      if ([204, 200].includes(response.status)) {
        this.showToastRecoveryTypeBySmsSuccess();
      }
    }).catch(() => {
      this.handlePasswordFailure();
    }).finally(() => {
      this.setStepNewPasswordInProgress(false);
    });
  }

  private handlePasswordFailure(): void {
    const message = `${MessageService.translate('patient.messages.server-error')}. ${MessageService.translate('patient.button.try-again')}`;
    showToastNotificationError(message);
  }

  private setStepRecoveryTypeInProgress(value: boolean): void {
    this.formRecoveryTypeAPI.setLoading(value);
  }

  private setStepNewPasswordInProgress(value: boolean): void {
    this.formPasswordAPI.setLoading(value);
  }

  @OnClick({ target: ['link-resend-sms'] })
  handleLinkSmsCodeOnClick(): void {
    this.panelLoaderResendSmsCodeAPI.setVisible(true);
    const formRecoveryTypeRecord = this.formRecoveryTypeAPI.getRecord() as FormRecoveryTypeProps;
    const formUsernameRecord = this.formUsernameAPI.getRecord() as unknown as FormUsernameProps;

    const isLaboratoryWebUser = this.context.get('isLaboratoryWebUser') as boolean;
    const patientId = this.context.get('patientId') as string;

    const resetPasswordWebLab: ResetPasswordWebLab = {
      isLaboratoryWebUser,
      patientId,
    }

    const body: PasswordResetProps = {
      captchaResponse: formRecoveryTypeRecord.captchaResponse,
      recoveryDocumentType: formUsernameRecord.documentType,
      recoveryKey: (formUsernameRecord.username ?? formUsernameRecord.physicalPersonCode) as string,
      recoveryKeyType: formUsernameRecord.identificationType.value,
      recoveryType: formRecoveryTypeRecord.recoveryType.value,
      resetPasswordWebLab
    }

    this.panelLinkResendCodeAPI.setVisible(false);
    ResetPasswordService.resendPasswordBySms(body, this.context.get('dsLogin') as string).then(() => {
      this.handleSuccessResendCode();
    }).catch(() => {
      this.panelLoaderResendSmsCodeAPI.setVisible(false);
      this.panelLinkResendCodeAPI.setVisible(true);
    });
  }

  handleSuccessResendCode(): void{
    this.panelLoaderResendSmsCodeAPI.setVisible(false);
    this.setCounter();
  }

  setCounter(): void {
    let counter = 0;
    this.panelLinkResendCodeAPI.setVisible(false);

    const countInterval = setInterval(() => {
      counter++;
      if (counter < 60) {
        if (!this.panelLinkResendCodeAPI.isVisible()) {
          const message = `${MessageService.translate('sms.resend.successful')}\n${MessageService.translate('account.confirmation.sms.wait.seconds')}`;
          this.textInformationResendSmsCode.setValue(message.replace('#@seg#@', String(60 - counter)));
        }
      } else {
        clearInterval(countInterval);
        this.panelLinkResendCodeAPI.setVisible(true);
        this.textInformationResendSmsCode.setValue(MessageService.translate('send.new.code.sms.available'));
      }
    }, 1000);

    this.intervalsToClear.set(WizardForgetPasswordSteps.STEP_RECOVERY_TYPE, countInterval);
  }

  @OnMount('field-confirm-password')
  handleFieldConfirmPasswordLoading(event: MountEvent): void {
    const fieldAPI = event.target as FieldAPI;
    fieldAPI.addLocalValidator( 'password-match-validator', async () => { return await this.comparePassword(this.fieldPasswordAPI, this.fieldConfirmPasswordAPI) } )
  }

  comparePassword(passwordField: FieldAPI, confirmPasswordField: FieldAPI ): Promise<Validation[]> {
    const password = passwordField.getValue();
    const confirmPassword = confirmPasswordField.getValue();
    const validMessage = 'newaccount.validator.password.confirm.valid';
    const notValidMessage = 'newaccount.validator.password.not.match'

    const name = 'password-match-validator'
    let message = '';
    let valid;

    if ( password && confirmPassword ) {
      valid = password === confirmPassword;
      message = valid ? validMessage : notValidMessage;
    } else {
      valid = true;
    }

    return Promise.resolve([{ message, name, valid }]);
  }

  @OnChange({ target: ForgetPasswordAliases.AUTOCOMPLETE_DOCUMENT_TYPE })
  handleChangeAutoCompleteDocumentType(event: ChangeEvent<DocumentType>): void {

    const documentType = event.value as DocumentType;
    this.fieldPhysicalPersonCodeAPI.setLabel(DocumentTypeDefinitions.getLabelExpression(documentType));
    this.fieldPhysicalPersonCodeAPI.removeValidator(FieldValidatorsName.MIN_LENGTH);
    this.inputPhysicalPersonCodeAPI.setValue('');
    this.inputPhysicalPersonCodeAPI.setLength(DocumentTypeDefinitions.getMaxLength(documentType));

  }
}

export default ForgetPasswordController;